import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Z_INDEX } from '@liveHelper/constants'
import type { UIState, WidgetState } from '@liveHelper/models/fsm/machine'
import { FadeInAnimation } from '@oneToOne/shared/styles'

export const WIDGET_SIZES = {
  minimized: {
    default: {
      width: '100px',
      height: '140px',
    },
    avatar: {
      width: '50px',
      height: '50px',
    },
    large_avatar: {
      width: '70px',
      height: '70px',
    },
  },
  expanded: {
    default: {
      width: '360px',
      height: '630px',
    },
  },
  fullscreen: {
    default: {
      width: '100vw',
      height: '100vh',
    },
    mobile: {
      width: '100dvw',
      height: '100dvh',
    },
  },
}

const NoSelect = css`
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                supported by Chrome, Edge, Opera and Firefox */
`

// Height and width should be explicit here for animation to work properly
export const TransitionContainer = styled.div<{
  transitionContainerHeight: string
  topLevelUIState: WidgetState
  uiState: UIState['value']
  isFullscreen: boolean
  hasLastUnreadMessage: boolean
  hasQueuePosition: boolean
  isAvatarIdleUI: boolean
  isLargeAvatar: boolean
  isMobileWebEnabled: boolean
}>`
  ${NoSelect}

  position: relative;
  display: flex;
  pointer-events: auto;

  width: ${({
    uiState,
    isFullscreen,
    topLevelUIState,
    hasLastUnreadMessage,
    hasQueuePosition,
    isAvatarIdleUI,
    isLargeAvatar,
  }) =>
    isFullscreen
      ? WIDGET_SIZES.fullscreen.default.width
      : uiState === 'idle_expanded' || uiState === 'idle_scheduling'
        ? WIDGET_SIZES.expanded.default.width
        : (uiState === 'idle_minimized' && hasLastUnreadMessage) ||
            (uiState === 'idle_minimized' &&
              isAvatarIdleUI &&
              !hasQueuePosition)
          ? isLargeAvatar
            ? WIDGET_SIZES.minimized.large_avatar.width
            : WIDGET_SIZES.minimized.avatar.width
          : uiState === 'dialing_minimized' ||
              (topLevelUIState === 'minimized' &&
                uiState !== 'request_microphone_access' &&
                uiState !== 'request_visitor_information' &&
                uiState !== 'appointment_lobby' &&
                uiState !== 'appointment_dialing' &&
                uiState !== 'dialing_expanded' &&
                uiState !== 'on_call_expanded')
            ? WIDGET_SIZES.minimized.default.width
            : [
                  'request_microphone_access',
                  'request_visitor_information',
                  'appointment_lobby',
                  'appointment_dialing',
                ].includes(uiState) ||
                (uiState === 'dialing_expanded' && !isFullscreen) ||
                (uiState === 'on_call_expanded' && !isFullscreen) ||
                topLevelUIState === 'chat'
              ? WIDGET_SIZES.expanded.default.width
              : 'inherit'};

  /* Fallback for browsers not supporting dvh */
  height: ${({ isFullscreen }) =>
    isFullscreen ? WIDGET_SIZES.fullscreen.default.height : 'inherit'};
  max-height: ${({ isFullscreen }) =>
    isFullscreen
      ? WIDGET_SIZES.fullscreen.default.height
      : 'calc(100vh - var(--spacing-16) - var(--live-helper-bottom))'};

  height: ${({ transitionContainerHeight }) => transitionContainerHeight};

  max-height: ${({ isFullscreen, isMobileWebEnabled }) =>
    isFullscreen && isMobileWebEnabled
      ? WIDGET_SIZES.fullscreen.mobile.height
      : isFullscreen && !isMobileWebEnabled
        ? WIDGET_SIZES.fullscreen.default.height // [OTO-1379] This is a hack to fix the fullscreen issue on safari desktop
        : `calc(${WIDGET_SIZES.fullscreen.mobile.height} - var(--spacing-16) - var(--live-helper-bottom))`};

  background: inherit;

  justify-content: flex-end;
  align-items: flex-end;
  flex-direction: column;
`

const BaseContainerStyles = css`
  box-shadow: var(--box-shadow);
  filter: brightness(1);
`

export const LiveHelperModal = styled.div<{
  uiState: UIState['value']
  isFullscreen: boolean
  hasFloatingMessage: boolean
  isPositionedLeft?: boolean
  isAvatarIdleUI?: boolean
  zIndex?: number
}>`
  position: fixed;
  z-index: ${({ zIndex }) => zIndex ?? Z_INDEX.BASE_WIDGET};

  bottom: ${({ isFullscreen }) =>
    isFullscreen ? '0' : 'var(--live-helper-bottom)'};

  ${({ isPositionedLeft, isFullscreen }) => {
    return isPositionedLeft
      ? `left: ${isFullscreen ? '0' : 'var(--spacing-16)'};`
      : `right: ${isFullscreen ? '0' : 'var(--spacing-24)'};`
  }}

  ${({ isFullscreen, isAvatarIdleUI, uiState }) =>
    isFullscreen
      ? null
      : uiState === 'idle_minimized' && isAvatarIdleUI
        ? null
        : BaseContainerStyles}

  border-radius: ${({ uiState, hasFloatingMessage }) =>
    hasFloatingMessage && uiState === 'idle_minimized'
      ? '50%'
      : 'var(--border-radius-md)'};
  background: ${({ isFullscreen }) =>
    isFullscreen ? 'var(--frosted-glass-dark-opaque)' : 'transparent'};

  ${({ isFullscreen }) =>
    isFullscreen
      ? `  
      height: 100vh;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    `
      : ''};
`

export const MessagePreviewWrapper = styled.div<{
  moreThanOneMessage: boolean
  modalHeight?: number
  isPositionedLeft?: boolean
  zIndex?: number
}>`
  ${FadeInAnimation}
  position: fixed;
  z-index: ${({ zIndex }) => zIndex ?? Z_INDEX.BASE_WIDGET};
  bottom: ${({ modalHeight, moreThanOneMessage }) =>
    moreThanOneMessage
      ? `calc(${
          modalHeight || 0
        }px + var(--live-helper-bottom) + var(--spacing-8) + var(--spacing-12))`
      : `calc(${
          modalHeight || 0
        }px + var(--live-helper-bottom) + var(--spacing-8))`};

  ${({ isPositionedLeft, moreThanOneMessage }) => {
    return isPositionedLeft
      ? `left: ${
          moreThanOneMessage ? 'var(--spacing-24)' : 'var(--spacing-16)'
        };`
      : `right: ${
          moreThanOneMessage ? 'var(--spacing-32)' : 'var(--spacing-24)'
        };`
  }}
  pointer-events: none;
`
